import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";
import { quotes, siteInfo } from "@/mocks/guagua";

const FORM_ADDR = "https://readdy.ai/api/form/dahfo9bp14a1h3mr6l1g";

const PRINCIPLES = [
  {
    icon: "ri-shield-check-line",
    title: "只整理公开信息",
    text: "所有合集都基于公开可见的节目内容、官方物料与公开讨论整理，属于主题化信息汇编，不传播未经证实的信息。",
  },
  {
    icon: "ri-eye-off-line",
    title: "不做隐私挖掘",
    text: "我们不涉及任何个人隐私内容，也不收录与话题无关的个人信息，讨论限定在公开的作品与活动范围内。",
  },
  {
    icon: "ri-links-line",
    title: "标注信息来源方向",
    text: "每份合集都会说明内容来自哪一类公开物料，并标注更新时间，方便读者自行判断信息的时效。",
  },
  {
    icon: "ri-emotion-happy-line",
    title: "鼓励理性围观",
    text: "我们提供背景与时间线，而不是结论。希望读者看完之后能形成自己的判断，而不是被单一视角带着走。",
  },
];

const STATS = [
  { value: "490+", label: "已整理合集" },
  { value: "6", label: "内容分类方向" },
  { value: "每周", label: "固定更新盘点" },
  { value: "10 分钟", label: "补齐一周进度" },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于吃瓜合集导航",
        url: "https://yoovx.cn/about",
        description: "了解吃瓜合集导航的品牌定位、内容原则、用户口碑与联系方式。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteInfo.name,
        url: "https://yoovx.cn/",
        email: siteInfo.email,
        description: siteInfo.intro,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://yoovx.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://yoovx.cn/about" },
        ],
      },
    ],
    []
  );

  usePageMeta({
    title: "关于我们 - 吃瓜合集导航的品牌定位与内容原则",
    description:
      "了解吃瓜合集导航的品牌定位、发展理念与内容原则：只整理公开信息、不做隐私挖掘、标注来源方向并鼓励理性围观，同时提供联系方式与反馈入口。",
    keywords: "吃瓜合集导航,关于我们,内容原则,网爆吃瓜,娱乐话题整理",
    path: "/about",
    jsonLd,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (message.length > 500) {
      setStatus("error");
      setFormError("留言内容请控制在 500 字以内。");
      return;
    }
    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写称呼、邮箱和想说的话，方便我们回复你。");
      return;
    }
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || isSpam || parsed?.code !== "OK") {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim() !== "" && !isSpam
            ? serverMsg
            : "提交没有成功，请稍后重试，或直接发邮件联系我们。"
        );
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功，请稍后重试。");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="w-full px-4 py-10 md:px-8 md:py-14">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-700">
                首页
              </Link>
              <i className="ri-arrow-right-s-line leading-none" aria-hidden="true"></i>
              <span className="text-foreground-800">关于我们</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-5xl">
              关于吃瓜合集导航
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {siteInfo.intro}
            </p>
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">
                <span className="h-1 w-5 rounded-full bg-primary-500" aria-hidden="true"></span>
                品牌介绍
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                把散落的瓜，整理成一份读得下去的清单
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                娱乐话题往往同时出现在很多平台，信息零散、时间线混乱，想跟上一个热点常常要翻十几个页面。吃瓜合集导航做的事情很简单：把同一个话题相关的公开信息收拢到一份合集里，按背景、时间线和看点重新排序。
              </p>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                我们希望它更像一份可以逐条读完的索引，而不是标题的堆砌。每份合集都会写清楚它在讲什么、大致按什么顺序发展，以及有哪些值得留意的细节。
              </p>

              <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-2">
                {STATS.map((stat) => (
                  <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100/60 p-4">
                    <p className="font-heading text-xl font-black text-primary-600 md:text-2xl">{stat.value}</p>
                    <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="overflow-hidden rounded-lg border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20minimal%20editorial%20flatlay%20of%20notebook%20color%20swatch%20cards%20and%20magnifier%20on%20cream%20desk%2C%20soft%20natural%20light%2C%20coral%20amber%20green%20accents%2C%20clean%20composition&width=1200&height=900&seq=chigua-about-01&orientation=landscape"
                  alt="吃瓜合集导航的内容整理工作场景"
                  title="吃瓜合集导航 - 内容整理与原则"
                  className="h-72 w-full object-top md:h-[420px]"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-secondary-100/50">
          <div className="w-full px-4 py-12 md:px-8 md:py-16">
            <SectionHeading
              align="center"
              eyebrow="内容原则"
              title="我们如何整理每一条内容"
              description="四条原则贯穿每一份合集的整理过程，也是我们判断内容能不能收录的标准。"
            />
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {PRINCIPLES.map((p, index) => (
                <div
                  key={p.title}
                  className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
                >
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${
                      index % 2 === 0 ? "bg-secondary-100 text-secondary-800" : "bg-primary-100 text-primary-700"
                    }`}
                  >
                    <i className={`${p.icon} text-xl leading-none`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950 md:text-lg">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <SectionHeading
            eyebrow="发展理念"
            title="先把内容做扎实，再谈其他"
            description="我们不追求信息投喂的速度，更在意读者看完之后是否真的理解了这件事的大致脉络。"
          />
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="font-heading text-3xl font-black text-accent-500">01</span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">按热度而非按噱头排序</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                榜单顺序参考讨论量与阅读完成度，而不是标题的夸张程度，把更值得读的内容放在更靠前的位置。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="font-heading text-3xl font-black text-accent-500">02</span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">保持稳定的更新节奏</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                一周盘点固定更新，热门事件随时补充。与其追逐每一分钟的波动，不如把节奏稳住，让读者形成预期。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="font-heading text-3xl font-black text-accent-500">03</span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">尊重每一位读者</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                不使用夸张标题获取点击，也不设置诱导性的跳转。页面干净、内容清楚，是我们对读者的基本尊重。
              </p>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-background-100/60 px-4 py-12 md:px-8 md:py-16">
          <SectionHeading
            align="center"
            eyebrow="用户口碑"
            title="看看常来的他们怎么说"
            description="来自读者们的真实使用感受，也是我们持续更新下去的动力。"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {quotes.map((q) => (
              <figure key={q.id} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                <i className="ri-double-quotes-l text-3xl text-primary-300 leading-none" aria-hidden="true"></i>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{q.text}</blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 font-heading text-sm font-bold text-accent-800">
                    {q.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-950">{q.name}</span>
                    <span className="block text-xs text-foreground-500">{q.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="联系我们"
                title="想说的都可以留言"
                description="无论是发现内容有出入、希望补充某个话题，还是想聊聊合作，都可以通过下面的表单或邮箱联系我们。"
              />
              <div className="mt-6 space-y-4">
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100/60 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-lg leading-none" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">联系邮箱</span>
                    <span className="block text-sm font-medium text-foreground-900">{siteInfo.email}</span>
                  </span>
                </a>
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100/60 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                    <i className="ri-time-line text-lg leading-none" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">更新节奏</span>
                    <span className="block text-sm font-medium text-foreground-900">一周盘点每周固定更新</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <form
                data-readdy-form
                id="contact-form-chigua"
                onSubmit={handleSubmit}
                className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
              >
                <h3 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">留言表单</h3>
                <p className="mt-1.5 text-sm text-foreground-600">带 * 的为必填项，我们通常会在几个工作日内回复。</p>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      称呼 *
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-300/60"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      邮箱 *
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="方便回复的邮箱"
                      className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-300/60"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="contact-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    留言类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    defaultValue="内容反馈"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-300/60"
                  >
                    <option value="内容反馈">内容反馈</option>
                    <option value="话题推荐">话题推荐</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div className="mt-4">
                  <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    想说的话 *
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请简单描述你的问题或建议，500 字以内"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-300/60"
                  ></textarea>
                </div>

                <div className="contact-alt-field" aria-hidden="true">
                  <label htmlFor="contact-website-alt">网站（请勿填写）</label>
                  <input
                    id="contact-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="submit"
                    className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-line leading-none" aria-hidden="true"></i>
                    提交留言
                  </button>
                  {status === "success" && (
                    <p className="inline-flex items-center gap-1.5 text-sm font-medium text-secondary-700">
                      <i className="ri-checkbox-circle-line leading-none" aria-hidden="true"></i>
                      已收到你的留言，感谢反馈！
                    </p>
                  )}
                  {status === "error" && formError && (
                    <p className="inline-flex items-start gap-1.5 text-sm font-medium text-primary-700">
                      <i className="ri-error-warning-line mt-0.5 leading-none" aria-hidden="true"></i>
                      {formError}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}